import { Typography } from '@mui/material';
import { Tool } from '@procureai/agents';
import { ChipList } from '@procureai/ui-mui';
import { ToolResult } from '@procureai/uix/ai';
import { ReactNode, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ChatLoadingIndicator } from '../ChatMessage/indicators/ChatLoadingIndicator';
import { SvbrgCatalogueFilter, SvbrgSearchCatalogueInput, SvbrgSearchCatalogueOutput } from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';
import { CatalogueDataGrid } from '~/routes/IntakeDetail/steps/IntakeExtendedLineItemsCheckStep/components/CatalogueDataGrid';

import { ToolResultComponent } from '.';
import { useCatalogueSearchResultFooterCta } from './CatalogueSearchTool.hook';

export const CatalogueSearchTool: ToolResultComponent<
  Tool.searchCatalogue,
  SvbrgSearchCatalogueInput,
  SvbrgSearchCatalogueOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools.${toolName}` });

  const { hasFooter, ...transProps } = useCatalogueSearchResultFooterCta();
  if (tool.state === 'output-available') {
    const toolMessage = t(`result`, {
      query: tool.input?.query,
      count: tool.output?.total ?? 0,
    });

    return (
      <ToolResult label={toolMessage} defaultOpen={false}>
        <div className="flex flex-col w-full gap-2">
          <FilterChipList args={tool.input} filters={tool.output.filters} />

          <CatalogueDataGrid rows={tool.output.items} />

          {hasFooter && (
            <Typography variant="body2" color="text.secondary">
              <Trans
                {...transProps}
                components={{ a: <a className="underline" rel="noopener noreferrer" target="_blank" /> }}
              />
            </Typography>
          )}
        </div>
      </ToolResult>
    );
  }

  // generate a thinking message from the tool invocation
  const toolMessage = t(`call`, { query: tool.input?.query });
  // no result yet, so we are in execution state
  return <ChatLoadingIndicator message={toolMessage} />;
};

interface FilterChipListProps {
  args: SvbrgSearchCatalogueInput;
  filters: SvbrgCatalogueFilter[];
}

export const FilterChipList = ({ args, filters }: FilterChipListProps): ReactNode => {
  // all applied filters as strings
  const chipData: string[] = useMemo(() => {
    const appliedFilters: string[] = [];

    // add query as first filter
    appliedFilters.push(`"${args.query}"`);

    // add manufacturers
    if (args.manufacturerFilter?.bucketIds?.length) {
      appliedFilters.push(args.manufacturerFilter.bucketIds.join(', '));
    }

    // add all dynamic filter values
    if (args.dynamicFacetFilters?.length) {
      for (const df of args.dynamicFacetFilters) {
        if (df.bucketIds?.length) {
          // find the filter to get the name of the bucket value
          const facetFilter = filters.find((f) => f.id === df.facetId);

          // map each bucket id to its name
          if (facetFilter) {
            const bucketNames = df.bucketIds.map((bucketId) => {
              // TODO: fix seven bridges JSON type, this is an array of objects with id, name, count
              const name = (
                facetFilter.values as unknown as {
                  id: string;
                  name: string;
                  count?: number;
                }[]
              ).find((bucket) => bucketId === bucket.id)?.name;

              return name ?? bucketId; // use name of value or fallback to id
            });

            if (bucketNames.length) {
              appliedFilters.push(bucketNames.join(', '));
            }
          } else {
            // fallback to just showing the ids
            appliedFilters.push(df.bucketIds.join(', '));
          }
        }
      }
    }

    // add price range if set
    if (args.priceFilter) {
      const { upperBound, lowerBound } = args.priceFilter;
      if (lowerBound != null && upperBound != null) {
        appliedFilters.push(`${lowerBound} - ${upperBound}`);
      } else if (lowerBound != null) {
        appliedFilters.push(`>= ${lowerBound}`);
      } else if (upperBound != null) {
        appliedFilters.push(`<= ${upperBound}`);
      }
    }

    return appliedFilters;
  }, [args, filters]);

  return <ChipList data={chipData} size="small" />;
};
